import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchDocuments,
  updateDocument,
  createDocument,
  deleteDocument,
} from "../api/document";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  order: {},
  totalCount: 0,
  currentCount: 0,
  newestDocumentsCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteDocumentAsync = createAsyncThunk(
  "documents/deleteDocumentAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteDocument(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_document");
    }
  }
);

export const createDocumentAsync = createAsyncThunk(
  "documents/createDocumentAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createDocument(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_document");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_document");
    }
  }
);

export const updateDocumentAsync = createAsyncThunk(
  "documents/updateDocumentAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateDocument(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "update_document");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_document");
    }
  }
);

export const getDocumentsAsync = createAsyncThunk(
  "documents/getDocumentsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchDocuments(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_documents");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_documents");
    }
  }
);

export const loadMoreDocumentsAsync = createAsyncThunk(
  "documents/loadMoreDocumentsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchDocuments(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_documents");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_documents");
    }
  }
);

const documentSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getDocumentsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.documents;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestDocumentsCount = action.payload.newestDocumentsCount;
      })
      .addCase(getDocumentsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente dokumentene.");
      })
      .addCase(loadMoreDocumentsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(loadMoreDocumentsAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = [...draftState.data, ...action.payload.documents];
          draftState.totalCount = action.payload.totalCount;
          draftState.currentCount =
            draftState.data.length + action.payload.currentCount;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(loadMoreDocumentsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente dokumentene.");
      })
      .addCase(updateDocumentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateDocumentAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.order = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateDocumentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere dokumentet.");
      })
      .addCase(createDocumentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createDocumentAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createDocumentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette dokumentet.");
      })
      .addCase(deleteDocumentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteDocumentAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (action.payload?.data) {
            draftState.data = action.payload.data;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteDocumentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette dokumentet.");
      });
  },
});

export const { reducer } = documentSlice;

export default documentSlice;
